import type { Messages } from '../messages';

/**
 * EN text for every reason code (C2).
 *
 * Wording is provisional until Q3 (tone, bilingualism; register inconsistency #3). Each text
 * stands on its own because the bot sends the same text in a notification (C2 spec R10).
 * Rules: say what happened, then what to do; no error number, no broker wording (FXM-04 §7);
 * never echo an input (I9).
 */
export const reasonEn: Messages['reason'] = {
  INTERNAL_ERROR: 'Something went wrong on our side. Please try again in a moment.',
  VALIDATION_FAILED: 'Some information is missing or incorrect. Check the highlighted fields.',
  NOT_FOUND: 'We couldn’t find this item. It may have been removed.',
  UNAUTHENTICATED: 'Your session has ended. Close FXMate and open it again from Telegram.',
  TOKEN_EXPIRED: 'Your session has expired. Close FXMate and open it again from Telegram.',
  // Provisional until Q22: "from Telegram" is true whether the trader enters through the bot or not.
  INIT_DATA_INVALID: 'We couldn’t confirm your Telegram identity. Open FXMate again from Telegram.',
  INIT_DATA_EXPIRED: 'FXMate was opened too long ago. Close it and open it again from Telegram.',
  // Client side only (S2): the API never sends it.
  INIT_DATA_MISSING: 'Open FXMate from Telegram.',
  IDEMPOTENCY_KEY_REQUIRED: 'Your request could not be processed. Please try again.',
  IDEMPOTENCY_KEY_REUSED: 'This request was already sent with different details. Please try again.',
  IDEMPOTENCY_KEY_IN_PROGRESS:
    'Your previous request is still being processed. Wait a moment, then check the result.',
  RATE_LIMITED: 'Too many attempts in a short time. Wait a moment, then try again.',
  SERVICE_UNAVAILABLE: 'FXMate is temporarily unavailable. Please try again in a few minutes.',

  // Provisional until Q70 (how the phone number is obtained).
  TELEGRAM_PHONE_NUMBER_INVALID: 'This number doesn’t match a Telegram account. Check it.',
  TELEGRAM_CODE_INVALID:
    'This code doesn’t match. Check the latest Telegram message and try again.',
  TELEGRAM_CODE_EXPIRED: 'This code has expired. Ask for a new code.',
  // Provisional until Q33 (message and delay).
  TELEGRAM_CODE_RATE_LIMITED: 'Telegram has limited code requests. Try again in {{minutes}} min.',
  TELEGRAM_PASSWORD_REQUIRED:
    'Your Telegram account uses two-step verification. Enter your Telegram password to continue.',
  TELEGRAM_PASSWORD_INVALID:
    'This Telegram password is incorrect. It is the one you chose in Telegram, not the 5-digit code.',
  TELEGRAM_LOGIN_NOT_STARTED: 'Your Telegram sign-in was interrupted. Ask for a new code.',
  TELEGRAM_ACCOUNT_MISMATCH:
    'This Telegram account isn’t the one you opened FXMate with. Use the same account.',
  TELEGRAM_UNAVAILABLE: 'Telegram isn’t responding. Try again in a moment.',
  TELEGRAM_SESSION_LOST:
    'FXMate is no longer connected to your Telegram account, perhaps after a logout on another device. Copying is suspended. Sign in again to resume.',

  CHANNEL_INACCESSIBLE:
    'This channel is no longer available: you left it or it was deleted. Copying from it has stopped.',
  SUBSCRIPTION_ALREADY_EXISTS: 'You already copy this channel.',
  CHANNEL_NOT_CONFIGURED: 'This channel copies nothing until you set it up.',
  CHANNEL_PAUSED:
    'This channel is paused. New signals are ignored; open positions are still tracked.',
  CHANNEL_UNREACHABLE:
    'FXMate has not been able to read this channel for several minutes. Its signals may be copied late or missed until reading resumes.',
  // FXM-02 §6 case 11: a suggestion, no penalty; the mode changes only if the trader saves new settings (I7).
  CONFIRMATION_UNANSWERED:
    'You haven’t answered any signal from this channel for 2 weeks. You can switch it to automatic mode in its settings; nothing changes if you stay in confirmation.',

  TRADING_ACCOUNT_CREDENTIALS_REJECTED:
    'Your broker refused the account number or the password. Check them and try again.',
  TRADING_ACCOUNT_SERVER_NOT_FOUND:
    'We couldn’t reach this server. Check the server name on the login screen of your MetaTrader app.',
  TRADING_ACCOUNT_READ_ONLY:
    'This password only lets you view the account, not place orders. Use your master password, the one that lets you trade in MetaTrader.',
  TRADING_ACCOUNT_LINKED_TO_OTHER_TRADER:
    'This trading account is already linked to another FXMate user.',
  // P1 (Q264, provisional): netting or exchange mode. An unreadable mode is a 503, not this code.
  TRADING_ACCOUNT_NETTING_NOT_SUPPORTED:
    'FXMate only copies to an MT5 account in “hedging” mode, where each trade stays a separate position. Your account is in “netting” mode: your trades and FXMate’s would be merged into a single position. Ask your broker for an account in hedging mode, then link it here.',
  TRADING_ACCOUNT_BALANCE_LOW:
    'Your balance is below the smallest order your broker accepts. As things stand, no trade can be copied.',
  TRADING_ACCOUNT_UNREACHABLE:
    'Your trading account is not responding. Your password may have changed at your broker. Copying is suspended until you reconnect the account.',
  TRADING_ACCOUNT_ALREADY_CONNECTED:
    'You already have a connected account. Disconnect it from Account to link another.',
  TRADING_ACCOUNT_NOT_CONNECTED:
    'No trading account is connected yet. Connect your MT5 account first.',
  // Provisional until Q17 and Q72 (who publishes the consent text versions).
  CONSENT_VERSION_UNKNOWN:
    'The authorization text has changed. Read the new version and confirm again.',

  SETTINGS_FALLBACK_SL_REQUIRED: 'Enter a fallback stop loss distance to save these settings.',
  SETTINGS_VERSION_CONFLICT:
    'These settings changed on another device. Reload to see the latest version.',
  RISK_PREVIEW_UNAVAILABLE: 'We can’t calculate your risk right now. Saving is unavailable.',
  // Provisional until Q19: the texts state the effect, not the calculation basis.
  DAILY_LOSS_LIMIT_APPROACHED: 'You have reached half of your maximum daily loss.',
  DAILY_LOSS_LIMIT_REACHED:
    'Your maximum daily loss is reached. No new position opens until midnight (Paris time). Closes and stop loss moves continue.',
  // Provisional until Q1 (inconsistency 1): FXM-02 §6 case 8 "copying is suspended", read as FXM-02 §4 (openings only).
  DAILY_LOSS_LIMIT_ALREADY_EXCEEDED:
    'Today’s loss already exceeds this limit: no new openings until midnight (Paris time). Closes continue.',
  MAX_POSITIONS_REACHED:
    'The maximum number of open positions for this channel is reached. New signals are not copied until a position closes.',

  SIGNAL_WITHOUT_STOP_LOSS:
    'This signal has no stop loss and your settings ignore such signals. It was not copied.',
  CONFIRMATION_EXPIRED: 'You didn’t confirm this signal in time. The trade was not taken.',
  CONFIRMATION_CANCELLED: 'You paused this channel, so the pending signal was cancelled.',
  CONFIRMATION_OBSOLETE:
    'The channel cancelled or closed this trade before you answered. Nothing was done.',
  CONFIRMATION_DECLINED: 'You ignored this signal. Nothing was done.',

  // M4: shown on the channel's row in E10, so the text needs no channel name.
  EMERGENCY_PAUSE_FAILED: 'This channel could not be paused. Try again.',
  POSITIONS_UNAVAILABLE:
    'We can’t show your positions, so closing is unavailable. Pausing still works.',
  // Provisional until Q40: FXM-04 §4 retries closes until they succeed; the engine does not yet.
  POSITION_CLOSE_FAILED:
    'Your broker has not closed this position yet. FXMate keeps trying automatically.',

  INSTRUMENT_NOT_FOUND:
    'This instrument is not available on your trading account. The trade was not taken.',
  INSTRUMENT_UNAVAILABLE:
    'Your broker does not allow trading this instrument right now. The trade was not taken.',
  MARKET_CLOSED:
    'The market for this instrument is closed. The trade was not taken and will not be placed later.',
  // I7 / FXM-02 §6 case 1: explain and suggest a viable setting; never change the size silently.
  SIZE_BELOW_MINIMUM:
    'With your settings, the position would be smaller than your broker’s minimum. The trade was not taken. Raise your risk or switch to a fixed lot.',
  SIZE_ABOVE_MAXIMUM:
    'With your settings, the position would be larger than your broker allows. The trade was not taken: FXMate never reduces a size on its own.',
  // W4 R7, Q161 (provisional): a fixed lot off the broker's step is refused, never rounded (I7).
  SIZE_NOT_ON_STEP:
    'Your fixed lot is not a multiple of your broker’s step. The trade was not taken: FXMate never rounds a size on its own. Choose a lot your broker accepts.',
  // W4 R9, Q162 (provisional): no order without the stop loss the channel gave.
  STOP_LOSS_ALREADY_PASSED:
    'The price had already passed the signal’s stop loss. The trade was not taken and will not be placed later.',
  INSUFFICIENT_MARGIN:
    'There is not enough free margin on your account to open this position. The trade was not taken.',
  ORDER_REJECTED:
    'Your broker refused the order, even after several attempts over 30 seconds. The trade was not taken.',
  TRADING_ACCOUNT_SERVER_NOT_ALLOWED:
    'For now, FXMate only places orders on demo accounts. This account is not on an allowed demo server, so no order was placed.',
  // W7 R36 (c), C2 task 11 (Proposal, to be validated; Q242): the ACCOUNT_PROBLEM notice only.
  TRADING_ACCOUNT_NOT_RESPONDING:
    'Your trading account has not been responding for a few minutes. FXMate cannot execute anything on your account until it responds; copying resumes on its own. If it lasts, check the connection in the Account tab.',
  MODIFICATION_REFUSED_BY_BROKER:
    'Your broker refused the new level asked for. Nothing was changed: the position keeps its current stop loss and take profit.',
  STOP_LOSS_REFUSED_BY_BROKER:
    'Your broker accepted the order but refused its stop loss, which was too close to the price. The position is open without a stop loss: keep an eye on it.',
  PARTIAL_CLOSE_ROUNDED_TO_FULL:
    'The partial close asked by the channel was below your broker’s minimum size, so the position was closed in full.',
  ACCOUNT_LIQUIDATED:
    'Your broker liquidated your account, so the order could not go through. Copying is suspended on this account.',
  // FXM-04 §8: no automatic catch-up of missed positions.
  // W7 §4.3 item 7, Q202 (Proposal, to be validated): true for an unreachable account (FXM-04 §5)
  // and for an opening older than three minutes (FXM-03 §6 case 10, W6 R6).
  INSTRUCTION_MISSED:
    'FXMate could not apply this instruction when the channel posted it, so it was skipped. Missed trades are not caught up later.',
  CLOSE_DELAYED:
    'Your trading account was unreachable when the channel asked to close this position. It was closed as soon as the account came back.',
  PRICE_GAP:
    'Your broker filled the order at a price clearly different from the one the channel announced. The position is kept: check it in Positions.',

  POSITION_TRACKING_LOST:
    'Your trading account is unreachable: FXMate no longer knows the real state of this position.',
  // FXM-04 §6 case 14: FXMate reports the gap and does not act on its own.
  POSITION_MISMATCH:
    'This position does not match what FXMate expected on your account. FXMate did not change it. Check it in MetaTrader.',
  TIMELINE_UNAVAILABLE: 'The channel’s messages are not available for this position.',

  CLOSED_BY_CHANNEL: 'Closed by the channel',
  CLOSED_BY_STOP_LOSS: 'Stop loss hit',
  CLOSED_BY_TAKE_PROFIT: 'Take profit hit',
  CLOSED_BY_TRADER: 'Closed by you in MetaTrader',
  // Provisional until Q71 (C1) and Q56 (M6).
  CLOSED_BY_EMERGENCY_STOP: 'Closed by the emergency stop',
  CLOSE_REASON_UNKNOWN: 'Reason unknown',
};

/** EN texts shown when a parameterised code arrives without a usable parameter (C2 spec R8). */
export const reasonFallbackEn: Messages['reasonFallback'] = {
  TELEGRAM_CODE_RATE_LIMITED: 'Telegram has limited code requests. Try again later.',
};
